import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Eye, EyeOff, Rocket, Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/apiClient";
import logo from "@/assets/logo.png";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Please fill in all fields to enter the arena");
      return;
    }
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Access granted. Welcome back!");
      const pendingInvite = sessionStorage.getItem("pending_invite");
      if (pendingInvite) { sessionStorage.removeItem("pending_invite"); navigate(`/invite/${pendingInvite}`, { replace: true }); return; }
      navigate(location.state?.from || "/dashboard", { replace: true });
    } catch (err) {
      toast.error(getErrorMessage(err, "Invalid credentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid-overlay flex flex-col">
      <div className="px-5 sm:px-10 py-5">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="back-home">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-5 pb-16">
        <div className="w-full max-w-md glass rounded-3xl p-8 sm:p-10 animate-in-up">
          <div className="flex flex-col items-center text-center mb-8">
            <img src={logo} alt="Pit Lane Lounge" className="h-28 w-auto object-contain mb-6" />
            <div className="chip mb-3" style={{ background: "rgba(0,229,255,0.1)", border: "1px solid rgba(0,229,255,0.4)", color: "#00e5ff" }}>
              <ShieldCheck className="w-3.5 h-3.5" /> Access Terminal
            </div>
            <p className="text-slate-400">Enter your credentials to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-300 mb-2">Email Address</label>
              <input
                type="email"
                className="input-cyber"
                placeholder="gamer@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-300 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  className="input-cyber pr-12"
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="login-password"
                  autoComplete="current-password"
                />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-cyan-400"
                  data-testid="toggle-password">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="neon-btn w-full py-4 text-base" data-testid="login-submit">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Authenticating...</> : <><Rocket className="w-5 h-5" /> Access Granted</>}
            </button>
          </form>

          <p className="text-center text-slate-400 mt-7">
            New to the arena?{" "}
            <Link to="/register" className="font-bold neon-cyan-text" data-testid="go-register">Join the Battle</Link>
          </p>
          <div className="relative my-6 flex items-center gap-3">
            <span className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Or</span>
            <span className="flex-1 h-px bg-white/10" />
          </div>
          <Link
            to="/book"
            data-testid="login-book-guest"
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-display font-bold uppercase tracking-widest text-sm transition-all"
            style={{ background: "rgba(8,11,22,0.6)", border: "1px dashed rgba(148,163,184,0.45)", color: "#cbd5e1" }}
          >
            Book Without An Account
          </Link>
        </div>
      </div>
    </div>
  );
}
